import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { AppDispatch, RootState } from "../../../store/store";
import { STATUS } from "../../../store/slices/OrganizationSlice";
import { BulkMoveResult, BulkSkippedUser } from "../../../store/slices/UsersV3Slice";
import { getOrganizationName } from "../Organizations/utils";
import { bulkMoveUsersAsync } from "../../../store/thunks/UsersV3Thunk";
import { UserSelection } from "../../../services/UsersV3Service";
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from "../../Catalyst/dialog";
import { Button } from "../../Catalyst/button";
import OrgSelect from "../../common/OrgSelect";
import { useToast } from "../../../contexts/ToastContext";
import ButtonSpinner from "./ButtonSpinner";
import SkippedUsersList from "./SkippedUsersList";
import { endSentence, pluralizeUsers } from "./utils";

interface MoveUsersDialogProps {
  open: boolean;
  onClose: () => void;
  selection: UserSelection;
  selectedCount: number;
  // The org every selected user is in; null when they come from several orgs
  // (or "all pages" is selected). Names the confirmation copy and is not
  // offered as a destination.
  sourceOrganization: { id: number; name: string | null } | null;
  // Called after the server accepted the move (even partially) so the page
  // clears the selection and refetches the list and stats.
  onCompleted: () => void;
}

type Step = "pick" | "confirm" | "result";

// Figma "Move to organization" → "Move N users to new organization?". The
// result step only appears when some users were skipped, e.g. because they
// already exist in the destination org. The page remounts this dialog (new
// `key`) and clears the bulk request state on every open, so each move starts
// fresh.
const MoveUsersDialog = ({ open, onClose, onCompleted, ...props }: MoveUsersDialogProps) => {
  // Frozen when the dialog opens (it is remounted on every open): the page
  // clears its selection on success, and the dialog must not re-render as
  // "Move 0 users" while it fades out.
  const [{ selection, selectedCount, sourceOrganization }] = useState(() => props);
  const dispatch = useDispatch<AppDispatch>();
  const { showSuccessToast } = useToast();
  const { organizationOptions, organizationOptionsStatus, bulkStatus, bulkError } = useSelector(
    (state: RootState) => state.UsersV3Store
  );

  const [step, setStep] = useState<Step>("pick");
  const [destinationId, setDestinationId] = useState<number | null>(null);
  const [skipped, setSkipped] = useState<BulkSkippedUser[]>([]);
  const [batchId, setBatchId] = useState<string | null>(null);

  const isMoving = bulkStatus === STATUS.LOADING;
  const destination = organizationOptions.find((org) => org.id === destinationId);
  const destinationName = destination ? getOrganizationName(destination) : "the organization";
  const sourceName = sourceOrganization ? getOrganizationName(sourceOrganization) : null;
  // Moving users into the org they are already in would only produce skips.
  const isSameAsSource = destinationId != null && destinationId === sourceOrganization?.id;

  const handleClose = () => {
    if (!isMoving) onClose();
  };

  const handleConfirm = async () => {
    if (destinationId == null || isSameAsSource || isMoving) return;
    const result = await dispatch(
      bulkMoveUsersAsync({ selection, destinationOrganizationId: destinationId })
    );
    // bulkError is rendered inline; keep the dialog open so the admin can retry.
    if (!bulkMoveUsersAsync.fulfilled.match(result)) return;

    const outcome = result.payload as BulkMoveResult;
    const skippedUsers = outcome?.skipped ?? [];
    if (outcome?.movedCount > 0) {
      showSuccessToast(
        "Users moved",
        `Moved ${pluralizeUsers(outcome.movedCount)} to ${endSentence(destinationName)}`
      );
    }
    onCompleted();
    if (skippedUsers.length > 0) {
      setSkipped(skippedUsers);
      setBatchId(outcome?.batchId ?? null);
      setStep("result");
    } else {
      onClose();
    }
  };

  const fromText = sourceName ? `from ${sourceName}` : "from their current organizations";

  return (
    // handleClose ignores backdrop/Escape mid-request so the admin is never
    // unsure whether the move ran.
    <Dialog open={open} onClose={handleClose} size="lg">
      {step === "pick" && (
        <>
          <div className="flex items-start justify-between gap-4">
            <div>
              <DialogTitle>Move to organization</DialogTitle>
              <DialogDescription>
                {selectedCount.toLocaleString("en-US")} {selectedCount === 1 ? "person" : "people"} selected
              </DialogDescription>
            </div>
            <button
              type="button"
              onClick={handleClose}
              aria-label="Close"
              className="shrink-0 rounded-md p-1 text-zinc-500 hover:bg-gray-100"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <DialogBody>
            <OrgSelect
              variant="list"
              options={organizationOptions}
              value={destinationId}
              onChange={setDestinationId}
              isLoading={organizationOptionsStatus === STATUS.LOADING}
              isError={organizationOptionsStatus === STATUS.FAILED}
              searchPlaceholder="Search organization"
            />
            {isSameAsSource && (
              <p className="mt-3 text-sm font-medium text-red-500">
                The selected users are already in {sourceName && endSentence(sourceName)} Choose a different
                organization.
              </p>
            )}
          </DialogBody>
          <DialogActions>
            <Button outline onClick={handleClose}>
              Cancel
            </Button>
            <Button
              color="green"
              disabled={destinationId == null || isSameAsSource}
              onClick={() => setStep("confirm")}
            >
              Move
            </Button>
          </DialogActions>
        </>
      )}

      {step === "confirm" && (
        <>
          <DialogTitle>Move {pluralizeUsers(selectedCount)} to new organization?</DialogTitle>
          <DialogDescription>
            You&rsquo;re about to move {pluralizeUsers(selectedCount)} {fromText} to {endSentence(destinationName)}{" "}
            Their account data &mdash; including profile, connected accounts, and spending plan &mdash;
            will be retained.
          </DialogDescription>
          {bulkError && <p className="mt-4 text-sm font-medium text-red-500">{bulkError}</p>}
          <DialogActions>
            <Button outline onClick={handleClose} disabled={isMoving}>
              Cancel
            </Button>
            <Button color="green" onClick={handleConfirm} disabled={isMoving}>
              {isMoving && <ButtonSpinner />}
              {isMoving ? "Moving..." : "Confirm"}
            </Button>
          </DialogActions>
        </>
      )}

      {step === "result" && (
        <>
          <DialogTitle>Some users were not moved</DialogTitle>
          <DialogBody>
            <SkippedUsersList title={`${pluralizeUsers(skipped.length)} skipped`} skipped={skipped} batchId={batchId} />
          </DialogBody>
          <DialogActions>
            <Button color="dark" onClick={handleClose}>
              Done
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default MoveUsersDialog;
